/**
 * Audit entries for corrections of saved results (v3.98.1).
 *
 * Until v3.98.1 no correction was recorded anywhere: not the Results grids'
 * double-click (Letter/Sound, NWF, PA 4K: type a total, override to N/A,
 * clear an N/A) and not the per-letter edit in the student pop-up. Kemal,
 * 2026-11-02: a correction records who changed what and when, with the old
 * and new score and answers, and is recorded even while an admin views the
 * app as a teacher (Logger's recordWhileViewingAs).
 *
 * The entry goes to audit_logs (admins read; nobody updates or deletes). It
 * is best-effort, like every other audit event: Logger swallows its own
 * errors, so a failed log never fails the correction, and the entry is
 * written after the correction succeeds — a separate write, so a dropped
 * connection between the two can leave a correction unrecorded (the
 * Fix-a-result v1 build is where an all-or-nothing batch belongs). The write
 * is tracked (pendingWrites), so sign-out waits for it. It holds ids, the
 * test, the quarter and the before/after score and answers; no name and no
 * note is copied in (a legacy studentId can itself contain a name).
 */
import { logAuditEvent } from '../services/Logger';
import { trackWrite } from './pendingWrites';

export const RESULT_AUDIT_ACTIONS = Object.freeze({
  // A saved score replaced by a typed total or N/A (Results grids).
  SCORE_OVERRIDDEN: 'RESULT_SCORE_OVERRIDDEN',
  // An N/A result deleted (Results grids, "clear this N/A").
  NA_CLEARED: 'RESULT_NA_CLEARED',
  // A total typed into an empty cell (Results grids) — a new result.
  MANUAL_ENTRY: 'RESULT_MANUAL_ENTRY',
  // One item (letter / vowel) changed in the student pop-up.
  ITEM_EDITED: 'RESULT_ITEM_EDITED',
});

// Firestore rejects undefined field values; every optional value is null.
const orNull = (v) => (v === undefined ? null : v);

/**
 * What a correction record keeps of a result: the score, whether it was a
 * typed total, the item answers (so a run the override wiped can be rebuilt)
 * and the number of items asked.
 */
export const resultSnapshot = (assessment) => {
  if (!assessment) return null;
  return {
    correctCount: orNull(assessment.correctCount),
    manualEntry: assessment.manualEntry === true,
    responses: assessment.responses || {},
    totalLetters: orNull(assessment.totalLetters),
  };
};

/**
 * Record one correction. `result` names the result changed: its id plus the
 * student, class, test and quarter (the quarter label of the slot, which a
 * legacy doc may not store itself). `before` / `after` are resultSnapshot()s
 * or null (null before = a new result; null after = a deleted one).
 */
export const auditResultChange = (user, action, result, { before = null, after = null, item = null } = {}) =>
  trackWrite(logAuditEvent(user, action, orNull(result && result.id), {
    collection: 'assessments',
    studentId: orNull(result && result.studentId),
    classId: orNull(result && result.classId),
    testType: orNull(result && result.testType),
    quarter: orNull(result && result.quarter),
    before,
    after,
    ...(item ? { item } : {}),
  }, { recordWhileViewingAs: true }));
